/**
 * 📄 MODAL GÉNÉRATION FACTURE LOGISTICS
 * ======================================
 * Modal pour générer une facture client à partir de lignes Job Costing sélectionnées
 *
 * Workflow:
 * 1. Utilisateur sélectionne lignes revenue/disbursement dans le tableau
 * 2. Saisit infos client (nom, adresse, email, téléphone)
 * 3. Définit dates (facture, échéance)
 * 4. Ajoute notes optionnelles
 * 5. Clique "Générer facture" → Appel API backend
 * 6. Facture créée → Auto-refresh Job Costing
 *
 * Contraintes:
 * - Seulement lignes type "revenue" ou "disbursement" sélectionnables
 * - Lignes déjà facturées (is_invoiced=true) désactivées
 * - Calcul automatique totaux (sous-total, taxes, total)
 *
 * @see src/gestion-logistics/hooks/useInvoices.ts (generateInvoiceFromJobCosting)
 * @see routes/logistics/invoices.js (POST /generate)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  Table,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Alert,
  message,
  Space,
  Typography,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import useInvoices from '../hooks/useInvoices';
import { JobCostingLine } from '../types/costing.types';

const { TextArea } = Input;
const { Text } = Typography;

interface InvoiceGeneratorModalProps {
  visible: boolean;
  onClose: () => void;
  shipmentId: number | null;
  jobCostingLines: JobCostingLine[];
  onSuccess?: () => void;
}

const InvoiceGeneratorModal: React.FC<InvoiceGeneratorModalProps> = ({
  visible,
  onClose,
  shipmentId,
  jobCostingLines,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { generateInvoiceFromJobCosting, loading } = useInvoices();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [invoiceType, setInvoiceType] = useState<'final' | 'proforma'>('final');

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  // Filtrer les lignes sélectionnables (revenue/disbursement non facturées)
  const selectableLines = useMemo(() => {
    return jobCostingLines.filter(
      (line) =>
        (line.line_type === 'revenue' || line.line_type === 'disbursement') &&
        !line.is_invoiced
    );
  }, [jobCostingLines]);

  // Calculer les totaux en temps réel
  const totals = useMemo(() => {
    const selectedLines = selectableLines.filter((line) =>
      selectedRowKeys.includes(line.id)
    );

    const subtotal = selectedLines.reduce(
      (sum, line) => sum + Number(line.amount || 0),
      0
    );

    const taxAmount = (subtotal * taxRate) / 100;
    const total = subtotal + taxAmount;

    return {
      subtotal,
      taxAmount,
      total,
      selectedCount: selectedLines.length,
    };
  }, [selectedRowKeys, selectableLines, taxRate]);

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedRowKeys([]);
      setTaxRate(0);
      setInvoiceType('final');
      form.setFieldsValue({ invoice_type: 'final' });
    }
  }, [visible, form]);

  useEffect(() => {
    if (invoiceType !== 'proforma') {
      return;
    }
    const blockedIds = new Set(
      jobCostingLines
        .filter((line) => line.proforma_invoice_id)
        .map((line) => line.id)
    );
    if (blockedIds.size === 0) {
      return;
    }
    setSelectedRowKeys((prev) =>
      prev.filter((key) => !blockedIds.has(Number(key)))
    );
  }, [invoiceType, jobCostingLines]);

  // Colonnes du tableau de sélection
  const columns: ColumnsType<JobCostingLine> = [
    {
      title: 'Type',
      dataIndex: 'line_type',
      key: 'line_type',
      width: 120,
      render: (type: string) => (
        <Text strong style={{ color: type === 'revenue' ? '#52c41a' : '#1890ff' }}>
          {type === 'revenue' ? 'Revenu' : 'Débours'}
        </Text>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.label_name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (qty: number, record) => `${qty || 1} ${record.unit || ''}`,
    },
    {
      title: 'Prix Unitaire',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (price: number) => `${formatAmount(price)} XOF`,
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ fontSize: '14px' }}>
          {formatAmount(amount)} XOF
        </Text>
      ),
    },
  ];

  // Configuration de la sélection
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: JobCostingLine) => ({
      disabled: Boolean(
        record.is_invoiced ||
        (record.line_type !== 'revenue' && record.line_type !== 'disbursement') ||
        (invoiceType === 'proforma' && record.proforma_invoice_id)
      ),
      name: record.label_name,
    }),
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    try {
      // Validation
      if (selectedRowKeys.length === 0) {
        message.warning('Veuillez sélectionner au moins une ligne');
        return;
      }

      const values = await form.validateFields();

      if (values.invoice_type === 'proforma') {
        const selectedIds = new Set(selectedRowKeys.map((key) => Number(key)));
        const blockedLines = jobCostingLines.filter(
          (line) => selectedIds.has(line.id) && line.proforma_invoice_id
        );
        if (blockedLines.length > 0) {
          message.warning('Certaines lignes sont deja liees a une proforma.');
          return;
        }
      }

      console.log('📄 [InvoiceGeneratorModal] Génération facture:', {
        shipmentId,
        selectedLines: selectedRowKeys.length,
        totals,
      });

      // Préparer les données pour l'API
      const invoiceData = {
        shipment_id: shipmentId!,
        job_costing_line_ids: selectedRowKeys.map((key) => Number(key)),
        invoice_date: values.invoice_date ? values.invoice_date.toISOString() : new Date().toISOString(),
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        invoice_type: values.invoice_type || 'final',
        tax_rate: taxRate,
        notes: values.notes || undefined,
        terms_conditions: values.terms_conditions || undefined,
      };

      // Appeler le hook
      const result = await generateInvoiceFromJobCosting(invoiceData);

      if (result) {
        message.success('Facture générée avec succès !');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('❌ [InvoiceGeneratorModal] Erreur validation:', error);
      message.error('Erreur lors de la génération de la facture');
    }
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Générer une Facture Client</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      okText="Générer la facture"
      cancelText="Annuler"
      onOk={handleSubmit}
      confirmLoading={loading}
      destroyOnClose
    >
      {/* Alerte informative */}
      <Alert
        message="Sélectionnez les lignes de revenu ou débours à facturer"
        description="Seules les lignes de type 'Revenue' et 'Débours' non encore facturées peuvent être sélectionnées."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Tableau de sélection des lignes */}
      <Card
        title="Lignes à facturer"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={selectableLines}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: 300 }}
          locale={{
            emptyText: 'Aucune ligne de revenu/débours disponible',
          }}
        />
      </Card>

      {/* Aperçu des totaux */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Lignes sélectionnées"
              value={totals.selectedCount}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Sous-total HT"
              value={totals.subtotal}
              formatter={(value) => `${formatAmount(value as number)} XOF`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="TOTAL TTC"
              value={totals.total}
              formatter={(value) => `${formatAmount(value as number)} XOF`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      <Divider>Détails de la facture</Divider>

      {/* Formulaire détails facture */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          invoice_type: 'final',
          invoice_date: dayjs(),
          due_date: dayjs().add(30, 'days'),
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Type de document"
              name="invoice_type"
              rules={[{ required: true, message: 'Type de document requis' }]}
            >
              <Select
                options={[
                  { value: 'final', label: 'Facture definitive' },
                  { value: 'proforma', label: 'Proforma (non facturant)' },
                ]}
                onChange={(value) => setInvoiceType(value)}
              />
            </Form.Item>
          </Col>
        </Row>

        {invoiceType === 'proforma' && (
          <Alert
            message="Proforma"
            description="Une proforma ne marque pas les lignes comme facturées et ne permet pas l'enregistrement de paiements."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={16}>
          {/* Dates */}
          <Col span={12}>
            <Form.Item
              label="Date de facture"
              name="invoice_date"
              rules={[
                { required: true, message: 'Date de facture requise' },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Sélectionner la date"
                prefix={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Date d'échéance"
              name="due_date"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Sélectionner la date d'échéance"
                prefix={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Taux de taxe */}
          <Col span={12}>
            <Form.Item
              label="Taux de taxe (%)"
              tooltip="Taux de TVA ou autre taxe applicable"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={2}
                value={taxRate}
                onChange={(value) => setTaxRate(value || 0)}
                placeholder="0"
                addonAfter="%"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Card size="small" style={{ marginTop: 30 }}>
              <Statistic
                title="Montant taxe"
                value={totals.taxAmount}
                formatter={(value) => `${formatAmount(value as number)} XOF`}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Item
          label="Notes"
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder="Notes ou remarques pour cette facture..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Conditions */}
        <Form.Item
          label="Conditions générales"
          name="terms_conditions"
        >
          <TextArea
            rows={2}
            placeholder="Conditions de paiement, garanties, etc."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>

      {/* Récapitulatif final */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
          <Row gutter={16} align="middle">
            <Col span={12}>
              <Text strong style={{ fontSize: '14px' }}>
                {totals.selectedCount} ligne(s) sélectionnée(s)
              </Text>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Space direction="vertical" size={0}>
                <Text type="secondary">Sous-total HT: {formatAmount(totals.subtotal)} XOF</Text>
                {taxRate > 0 && (
                  <Text type="secondary">
                    Taxe ({taxRate}%): {formatAmount(totals.taxAmount)} XOF
                  </Text>
                )}
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  TOTAL TTC: {formatAmount(totals.total)} XOF
                </Text>
              </Space>
            </Col>
          </Row>
        </Card>
      )}
    </Modal>
  );
};

export default InvoiceGeneratorModal;
